<template>
  <b-container class="RuleList">
    <b-row class="mb-3">
      <b-col>
        <h1>
          Alert Rule List
        </h1>
      </b-col>
      <b-col cols="4" align-self="center" class="text-right">
        <b-button
          data-cy="RuleList-createBtn"
          variant="primary"
          :disabled="!canCreateRules"
          :to="{ name: 'alert-rule-create' }"
          >New Alert Rule</b-button
        >
      </b-col>
    </b-row>
    <Table
      data-cy="RuleList-table"
      v-if="canSearchRules"
      :items="items"
      :fields="fields"
      :filterable="true"
      :selectable="false"
      :current-page="currentPage"
      :per-page="itemsPerPage"
      :total-rows="totalRows"
      @sort-changed="onSortChanged"
      @filtered="onFilterChanged"
      @page-changed="onPageChanged"
      @per-page-changed="onPerPageChanged"
    >
      <template #empty>
        <div v-if="loading"><h4 class="text-center mt-3">Loading...</h4></div>
        <div v-else>
          <h4 class="text-center mt-3">No Alert Rules to show.</h4>
          <p class="text-center">
            You can create a new Rule by clicking the button on the top-right
            corner, or try changing the filters to modify your search.
          </p>
        </div>
      </template>
      <template v-slot:cell(id)="data">
        <code>{{ data.item.id }}</code>
      </template>
      <template v-slot:cell(actions)="data">
        <b-button
          class="ml-2"
          variant="link"
          title="Edit Rule"
          :data-cy="`RuleListItem__${data.item.id}-editBtn`"
          :disabled="!canEditRules"
          :to="{
            name: 'alert-rule-update',
            params: { id: data.item.id }
          }"
          ><i class="fas fa-pen"></i
        ></b-button>
        <b-button
          class="ml-2"
          variant="link"
          title="Delete Rule"
          :disabled="!canDeleteRules"
          :data-cy="`RuleListItem__${data.item.id}-deleteBtn`"
          @click.prevent="onDeleteItem(data.item.id)"
          ><i class="fas fa-trash"></i
        ></b-button>
      </template>
    </Table>
    <ListNotAllowed v-else />
  </b-container>
</template>

<style lang="sass"></style>

<script>
import ListNotAllowed from '@/components/ListNotAllowed';
import { mapGetters } from 'vuex';
import { isActionAllowed } from '@/helpers/rights';
import { ALERT_RULES_COLLECTION } from '@/helpers/alertRules';
import throttle from 'lodash/throttle';

export default {
  name: 'RuleList',
  components: {
    ListNotAllowed
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 15,
      items: [],
      loading: false,
      totalRows: 0,
      sort: null,
      filter: ''
    };
  },
  computed: {
    ...mapGetters('tenants', ['selectedTenant']),
    ...mapGetters('auth', ['currentUserRights']),
    currentIndex() {
      if (!this.selectedTenant) {
        return null;
      }
      return this.selectedTenant._id;
    },
    canSearchRules() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'search',
        this.currentIndex,
        ALERT_RULES_COLLECTION
      );
    },
    canCreateRules() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'create',
        this.currentIndex,
        ALERT_RULES_COLLECTION
      );
    },
    canEditRules() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'replace',
        this.currentIndex,
        ALERT_RULES_COLLECTION
      );
    },
    canDeleteRules() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'delete',
        this.currentIndex,
        ALERT_RULES_COLLECTION
      );
    },
    fields() {
      return [
        {
          key: 'name',
          label: 'Alert name',
          sortable: true,
          tdClass: 'centeredCell'
        },
        {
          key: 'description',
          label: 'Description',
          sortable: false,
          tdClass: 'centeredCell'
        },
        {
          key: 'actions',
          label: '',
          sortable: false
        }
      ];
    },
    filerableFields() {
      return ['name.text', 'description'];
    },
    throttledFetchItems() {
      return throttle(this.fetchItems, 400);
    }
  },
  methods: {
    async onPageChanged(page) {
      this.currentPage = page;
      await this.fetchItems();
    },
    async onPerPageChanged(value) {
      this.itemsPerPage = value;
      this.currentPage = 1;
      await this.fetchItems();
    },
    async onFilterChanged(receivedFilter) {
      this.filter = receivedFilter;
      await this.throttledFetchItems();
    },
    async onSortChanged(data) {
      this.sort = [
        {
          [data.sortBy]: {
            order: data.sortDesc ? 'desc' : 'asc'
          }
        }
      ];
      await this.fetchItems();
    },
    async fetchItems() {
      if (!this.selectedTenant) {
        return;
      }
      const params = {};
      if (this.filter) {
        params.query = {
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: this.filerableFields
          }
        };
      }
      if (this.sort) {
        params.sort = this.sort;
      }
      this.loading = true;
      try {
        const result = await this.$kuzzle.document.search(
          this.selectedTenant._id,
          ALERT_RULES_COLLECTION,
          params,
          {
            from: (this.currentPage - 1) * this.itemsPerPage,
            size: this.itemsPerPage
          }
        );
        this.totalRows = result.total;
        this.items = result.hits.map(hit => ({
          ...hit._source,
          id: hit._id
        }));
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while loading the rules',
          variant: 'warning',
          toaster: 'b-toaster-bottom-right',
          appendToast: true,
          dismissible: true,
          noAutoHide: true
        });
      }
      this.loading = false;
    },
    async onDeleteItem(id) {
      const value = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this alert rule?',
        {
          title: 'Please confirm',
          footerClass: 'DeleteModal-footer'
        }
      );
      try {
        if (value) {
          await this.$kuzzle.document.delete(
            this.currentIndex,
            ALERT_RULES_COLLECTION,
            id,
            { refresh: 'wait_for' }
          );
          await this.fetchItems();
        }
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(
          'The complete error has been logged to the console',
          {
            title: 'Something went wrong while deleting the rule',
            variant: 'warning',
            toaster: 'b-toaster-bottom-right',
            appendToast: true,
            dismissible: true,
            noAutoHide: true
          }
        );
      }
    }
  },
  async mounted() {
    await this.fetchItems();
  },
  watch: {
    async selectedTenant() {
      await this.fetchItems();
    }
  }
};
</script>

<style lang="sass">
.centeredCell
  padding: 11px 10px !important
</style>
